import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useCart, formatPrice } from '../../hooks/useCart';

const CheckoutCancelPage: React.FC = () => {
  const navigate = useNavigate();
  const { items: cartItems, totalPrice } = useCart();

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CancelIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          
          <Typography variant="h4" gutterBottom>
            Payment Cancelled
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            Your cart has been saved and you can complete your purchase anytime.
          </Typography>
        </Box>

        {cartItems.length > 0 && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Your cart items are still saved. You can return to checkout whenever you're ready.
            </Alert>

            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Your cart contains:
                </Typography>
                
                <List dense>
                  {cartItems.map((item, index) => (
                    <React.Fragment key={item.marketId}>
                      <ListItem>
                        <ListItemText
                          primary={`• ${item.marketName}`}
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {item.productId.includes('monthly') ? 'Monthly' : 'Yearly'} - {formatPrice(item.price)}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < cartItems.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">
                    Total:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatPrice(totalPrice)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {cartItems.length > 0 && (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/checkout')}
              startIcon={<ShoppingCartIcon />}
              sx={{ minWidth: 180 }}
            >
              Return to Checkout
            </Button>
          )}
          
          <Button
            variant={cartItems.length > 0 ? 'outlined' : 'contained'}
            size="large"
            onClick={() => navigate('/global-market-reports')}
            startIcon={<StorefrontIcon />}
          >
            Continue Browsing
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <HelpOutlineIcon fontSize="small" />
            Need help? Contact our support team at support@horwath-htl.com
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CheckoutCancelPage;